import React, { useEffect, useRef } from 'react';
import {
  RailsBlock,
  RailsTile,
  RailsTileVersion,
} from '@sls-react/typings/services/rails/tile';
import { HeroImage } from '@sls-react/components/Homepage/Elements';
import useMobile from '@sls-react/hooks/useMobile';
import IosAppStoreLink from '@sls-react/components/Links/IosAppStoreLink';
import Image from '@sls-react/components/Image';
import Stars from '@sls-react/static/svg/Stars';
import { Button } from '@sls-react/components/Form/Button';
import RegisterLink from '@sls-react/components/Links/RegisterLink';
import EmailLoginButton from '@sls-react/components/LoginRegister/EmailLoginButton';
import { useSession } from '@sls-react/contexts/SessionContext';
import GooglePlayStoreLink from '@sls-react/components/Links/GooglePlayStoreLink';
import PlayStoreIcon from '@sls-react/static/svg/PlayStoreIcon';
import A from '@sls-react/components/Links/A';
import classNames from 'classnames';
import { Indicator } from '@sls-react/components/Swipeable/Elements';
import SwipeableViews from 'react-swipeable-views';
import {
  CarouselNextButton,
  CarouselPreviousButton,
} from '@sls-react/components/ItemDetails/elements';
import HomepageEmpowerHero from '@sls-react/components/Homepage/HomepageEmpowerHero';
import useCarousel from '@sls-react/hooks/useCarousel';
import FacebookLoginButton from '@sls-react/components/LoginRegister/FacebookLoginButton';
import IOS_APP_STORE from '@sls-react/static/ios-app-store.png';

const BaseHeroContent: React.FC<{
  androidLink: any;
  tile: RailsTile;
  version?: RailsTileVersion;
}> = ({ androidLink, tile, version }) => {
  const mobile = useMobile();
  const { loggedIn } = useSession();

  if (version ? version.image_type === 'blank' : tile.image_type === 'blank') {
    return (
      <div
        className="container"
        css={`
          min-height: 300px;
          @media (min-width: 767px) {
            min-height: 500px;
          }
        `}
      >
        <A
          href={tile.destination || '#'}
          css={`
            position: absolute;
            top: 0;
            left: 0;
            height: 100%;
            width: 100%;
            background: transparent;
          `}
        />
      </div>
    );
  }

  let buttons: React.ReactNode = null;

  if (loggedIn || version?.image_type === 'app_logo') {
    if (version?.button_text) {
      buttons = (
        <A
          href={tile.destination || '#'}
          className={mobile ? 'relative block mb-4' : ''}
        >
          <Button
            className="w-full"
            css={`
              background-color: ${version?.button_color || '#02c874'};
              border-color: ${version?.button_color || '#02c874'};
            `}
          >
            {version?.button_text}
          </Button>
        </A>
      );
    }
  } else if (version?.image_type === 'join_prompt') {
    buttons = (
      <React.Fragment>
        <RegisterLink
          className="relative"
          modalProps={{ defaultView: 'registerForm' }}
        >
          <EmailLoginButton className="mb-3" text="JOIN WITH EMAIL" />
        </RegisterLink>

        <FacebookLoginButton text="JOIN WITH FACEBOOK" />
      </React.Fragment>
    );
  }

  return (
    <div
      className={classNames(
        'container flex px-12 md:px-4 py-8 md:py-12',
        version?.halignment === 'left'
          ? 'text-left justify-start'
          : version?.halignment === 'center'
          ? 'text-center justify-center'
          : version?.halignment === 'right'
          ? 'justify-end'
          : '',
      )}
      css={`
        min-height: 300px;
        @media (min-width: 767px) {
          min-height: 500px;
        }
      `}
    >
      <div
        className="flex flex-col justify-center"
        css={`
          z-index: 1;
        `}
      >
        <A
          href={tile.destination || '#'}
          css={`
            position: absolute;
            top: 0;
            left: 0;
            height: 100%;
            width: 100%;
            background: transparent;
          `}
        />

        <h1
          css={`
            ${!mobile && 'font-size: 2.75rem;'};
            color: ${version?.text_color || '#fff'};
          `}
          className="mb-1 text-4xl font-bold"
        >
          {tile.title}
        </h1>

        {tile.subtitle && (
          <h1
            css={`
              color: ${version?.text_color || '#fff'};
            `}
            className="mb-4 text-xl font-bold"
          >
            {tile.subtitle}
          </h1>
        )}

        <div
          css={`
            width: 100%;
            @media (min-width: 767px) {
              width: 350px;
            }
          `}
          className={version?.halignment === 'center' ? 'mx-auto' : ''}
        >
          {mobile ? buttons : <div className="mb-6">{buttons}</div>}
          <div className="flex flex-col-reverse items-center justify-between mb-4 md:flex-row">
            <div className="flex items-center mt-2 md:mt-0 md:mr-8">
              <IosAppStoreLink source="homepage" className="relative mr-4">
                <Image
                  className="inline-block md:mr-8"
                  height={32}
                  width={100}
                  src={IOS_APP_STORE}
                  alt="iOS App Store"
                />
              </IosAppStoreLink>

              {androidLink}
            </div>

            <div className="flex flex-col text-center">
              <Stars className="mb-1" />
              <div
                className="text-xs"
                css={`
                  color: ${version?.text_color || '#fff'};
                `}
              >
                18k+ Ratings
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

interface Props {
  block: RailsBlock;
}

const HomepageCmsHero: React.FC<Props> = ({ block }) => {
  const mobile = useMobile();

  const { index, goToNextIndex, goToPreviousIndex, setIndex } = useCarousel(
    block.tiles?.length!,
  );

  const interval = useRef<any>(null);
  useEffect(() => {
    // Don't loop from end to start
    if (block.layout === 'carousel' && index < block.tiles?.length! - 1) {
      const int = setInterval(goToNextIndex, 8000);
      interval.current = int;
      return () => clearInterval(int);
    }
  }, [index]);

  const androidLink = (
    <GooglePlayStoreLink className="relative inline-flex mx-0 sm:mx-auto">
      <PlayStoreIcon height={32} width={106} />
    </GooglePlayStoreLink>
  );

  if (block.layout === 'carousel') {
    return (
      <>
        <section className="relative mb-4 md:mb-6">
          <SwipeableViews
            index={index}
            onChangeIndex={setIndex}
            animateHeight
            css={`
              min-height: 300px;
              @media (min-width: 767px) {
                min-height: 500px;
              }
            `}
          >
            {block.tiles?.map(tile => {
              let version = tile.versions?.find(v => v.platform === 'web');
              if (mobile) {
                const mobileVersion = tile.versions?.find(
                  v => v.platform === 'mobile',
                );
                if (mobileVersion) {
                  version = mobileVersion;
                }
              }

              return (
                <div key={tile.id} className="relative">
                  <HeroImage
                    useFastly={{
                      height: 700,
                    }}
                    src={
                      version
                        ? version.background_image_url
                        : tile.background_image_url
                    }
                  />

                  {version?.image_type === 'empower_athletes' ? (
                    <HomepageEmpowerHero
                      tile={tile}
                      block={block}
                      version={version}
                    />
                  ) : (
                    <BaseHeroContent
                      androidLink={androidLink}
                      tile={tile}
                      version={version}
                    />
                  )}
                </div>
              );
            })}
          </SwipeableViews>

          {block.tiles?.length! > 1 && (
            <>
              <CarouselPreviousButton onClick={goToPreviousIndex} />
              <CarouselNextButton onClick={goToNextIndex} />
            </>
          )}
        </section>

        {block.tiles?.length! > 1 && (
          <div className="flex justify-center my-4 space-x-2">
            {block.tiles?.map((t, i) => (
              <button key={t.id} onClick={() => setIndex(i)} className="block">
                <Indicator key={t.id} selected={i === index} />
              </button>
            ))}
          </div>
        )}
      </>
    );
  }

  const tile = block?.tiles?.[index]; // it won't always be 0
  let version: undefined | RailsTileVersion;

  if (mobile) {
    version = tile?.versions?.find(v => v.platform === 'mobile');
  } else {
    version = tile?.versions?.find(v => v.platform === 'web');
  }

  if (!tile || !version) {
    return null;
  }

  return (
    <section className="relative mb-4 md:mb-6">
      <HeroImage
        useFastly={{
          height: 700,
        }}
        src={version ? version.background_image_url : tile.background_image_url}
      />

      {/*justify-around px-6 pt-6 pb-4 md:w-3/5 lg:w-2/5 xl:px-10 md:py-10*/}
      <BaseHeroContent
        androidLink={androidLink}
        tile={tile}
        version={version}
      />
    </section>
  );
};

export default HomepageCmsHero;
